import { AsyncButton } from '@c42/component-library';
import { AsyncState, isLoading } from '@c42/constants';
import CoreClient from '@c42/core-client';
import { ComplexityError, validatePassword } from '@c42/password-complexity';
import { TextField } from '@mui/material';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router';
import Text from '../../shared/Text';
import { LOGIN_INDEX } from '../constants/routes';
import { sendEmptyTextIfNull } from '../utils/input-handlers';
import FormValidationMessage from './form-validation-message';
import { PasswordComplexity } from './password-complexity';
import { useSetResetPasswordData } from './providers/resetPassword/hooks';
import { useRoutingData, useSetInitialQueryParams } from './providers/routing/hooks';
import { FormFieldWrapper, FormSubmitWrapper, FormWrapper } from './shared-form-components';

const ResetPasswordWrapper: React.FC = () => {
  const location = useLocation();
  const setInitialQueryParams = useSetInitialQueryParams();

  React.useEffect(() => {
    const queryParams = Object.fromEntries(new URLSearchParams(location.search));
    // in case the user was already on the login app and copied/pasted
    // a reset url to this tab, we need to re-initialize the query params in state
    setInitialQueryParams(queryParams);
  }, []);

  return <ResetPassword />;
};

const ResetPassword: React.FC = () => {
  const [password, setPassword] = React.useState(null);
  const [confirmPassword, setConfirmPassword] = React.useState(null);
  const [submitState, setSubmitState] = React.useState(AsyncState.NOT_STARTED);
  const [error, setError] = React.useState<string>();

  const history = useHistory();
  const routingData = useRoutingData();
  const setResetPasswordData = useSetResetPasswordData();

  const complexityErrors = validatePassword(password);

  const isConfirmPasswordEmpty = !confirmPassword;
  const isPasswordMatching = password === confirmPassword;
  const isConfirmPasswordInvalid = confirmPassword != null && (isConfirmPasswordEmpty || !isPasswordMatching);
  const isFormValid = !(complexityErrors.length || !isPasswordMatching);

  let passwordErrorText;
  if (complexityErrors.indexOf(ComplexityError.EMPTY) >= 0) {
    passwordErrorText = Text.get('_validate_password_required');
  } else if (complexityErrors.length) {
    passwordErrorText = Text.get('_validate_password_requirements');
  }

  let confirmPasswordErrorText;
  if (isConfirmPasswordInvalid && isConfirmPasswordEmpty) {
    confirmPasswordErrorText = Text.get('_validate_password_confirm_required');
  } else if (isConfirmPasswordInvalid && !isPasswordMatching) {
    confirmPasswordErrorText = Text.get('login_error_passwordConfirm');
  }

  const submitForm = (password: string) => {
    setSubmitState(AsyncState.LOADING);
    const params = {
      token: routingData.passwordResetTokenParam,
      password,
    };
    CoreClient.UserPasswordResetUpdate.update({ params, useCache: false }).then(
      () => {
        setResetPasswordData((current) => ({
          ...current,
          notification: Text.get('login_password_reset_success'),
        }));
        history.push(LOGIN_INDEX);
      },
      (error) => {
        setSubmitState(AsyncState.ERROR);
        if (error?.responseJson?.[0]?.name === 'INVALID_TOKEN') {
          setError(Text.get('login_password_reset_invalid_token'));
        } else {
          setError(Text.get('login_password_reset_error'));
        }
      },
    );
  };

  const submitHandler = (
    submitForm: (password: string) => void,
    password: string | null,
    e: React.FormEvent,
  ) => {
    e.preventDefault();
    submitForm(password);
  };

  const changePasswordHandler: React.FormEventHandler = (event) => {
    event.persist?.();
    setPassword((event.target as HTMLInputElement).value);
  };

  const changeConfirmPasswordHandler: React.FormEventHandler = (event) => {
    event.persist?.();
    setConfirmPassword((event.target as HTMLInputElement).value);
  };

  return (
    <FormWrapper>
      <form onSubmit={submitHandler.bind(null, submitForm, password)} name="resetPasswordFrm" noValidate>
        <FormValidationMessage message={error} />

        <FormFieldWrapper>
          <TextField
            autoComplete="off"
            disabled={isLoading(submitState)}
            error={Boolean(passwordErrorText)}
            fullWidth
            helperText={passwordErrorText}
            inputProps={{ 'data-test': 'password-input' }}
            label={Text.get('login_new_password')}
            onBlur={sendEmptyTextIfNull.bind(null, changePasswordHandler, password)}
            onChange={changePasswordHandler}
            type="password"
            value={password || ''}
          />
        </FormFieldWrapper>

        <FormFieldWrapper>
          <TextField
            autoComplete="off"
            disabled={isLoading(submitState)}
            error={Boolean(confirmPasswordErrorText)}
            fullWidth
            helperText={confirmPasswordErrorText}
            inputProps={{ 'data-test': 'password-confirm-input' }}
            label={Text.get('login_confirm_new_password')}
            onBlur={sendEmptyTextIfNull.bind(null, changeConfirmPasswordHandler, confirmPassword)}
            onChange={changeConfirmPasswordHandler}
            type="password"
            value={confirmPassword || ''}
          />
        </FormFieldWrapper>

        <PasswordComplexity errors={complexityErrors} />

        <FormSubmitWrapper>
          <AsyncButton
            data-test="submit"
            disabled={isLoading(submitState) || !isFormValid}
            isLoading={isLoading(submitState)}
            type="submit"
            variant="contained"
          >
            {Text.get('login_reset_password')}
          </AsyncButton>
        </FormSubmitWrapper>
      </form>
    </FormWrapper>
  );
};

export default ResetPasswordWrapper;
